/// <amd-module name="Build/Designer/SavePanel" />
import * as React from "react";

import { FileList, IFileListItem } from "Build/Designer/Components/FileList";
import * as Resources from "Build/Designer/Resources";
import { CommitDescriptor } from "Build/Designer/YamlFileService";

import { equals, format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { TitleSize } from "VSSUI/Header";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerOrientation } from "VSSUI/Spinner";
import { Status, StatusSize, Statuses } from "VSSUI/Status";
import { TextField } from "VSSUI/TextField";
import { getSafeIdSelector } from "VSSUI/Util";
import { ValidationErrors } from "./Components/ValidationErrors";

enum CommitTo {
    DefaultBranch = "DefaultBranch",
    NewBranch = "NewBranch"
}

interface Props {
    fileName: string;
    commitMessage?: string;
    defaultBranch: string;
    errorMessage?: string;
    isOpen: boolean;
    isSaving: boolean;
    isRunning?: boolean;
    saveAndRun: boolean;
    canCreatePr: boolean; // Remove with Build2.BitbucketAzurePipelinesOAuthClient
    onSave: (commitDescriptor: CommitDescriptor) => void;
    onDismiss: () => void;
    filesAdded?: IFileListItem[];
    showCancelButton?: boolean;
    isValidateAndSave?: boolean;
    onValidate?: () => Promise<any | undefined>;
}

interface State extends CommitDescriptor {
    newBranchNameErrorMessage: string;
    validationErrors: string[];
    fetchedValidationErrors: boolean;
    saveAnywayClicked: boolean;
}

/**
 * Save/Commit Panel for the YAML Editor page of the CI Design wizard.
 */
export class SavePanel extends VssComponent<Props, State, IVssComponentContext> {
    private readonly _maxGitRefNameLength = 400; // VC GitConstants.MaxGitRefNameLength
    private readonly _invalidRefNameStrings: string[] = ["~", "^", ":", " ", "?", "*", "[", "\\", "/.", ".lock/", "//", "..", "@{"];
    private readonly _invalidLeadNameStrings: string[] = ["/", "."];
    private readonly _invalidTrailNameStrings: string[] = [".lock", ".", "/"];
    private readonly _commitInputId = "commitInputId";
    private isValidateAndSavePipelineByDefaultEnabled: boolean;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            message: this.props.commitMessage || Resources.SavePanelCommitMessage,
            description: "",
            createNewBranch: false,
            newBranchName: Resources.SavePanelNewBranchName,
            newBranchNameErrorMessage: "",
            createPullRequest: true,
            validationErrors: [],
            fetchedValidationErrors: false,
            saveAnywayClicked: false
        };

        this.isValidateAndSavePipelineByDefaultEnabled = isFeatureFlagEnabled(context.pageContext, "Build2.ValidateAndSavePipelineByDefault", false);
    }

    public componentDidMount() {
        if (this.isValidateAndSavePipelineByDefaultEnabled && this.props.isValidateAndSave && this.props.onValidate) {
            this.props.onValidate().then((result: any) => {
                var validationErrors = result.errors ? result.errors.split("\n") : [];
                this.setState({fetchedValidationErrors: true, validationErrors})
            });
        }
    }

    public componentDidUpdate(prevProps: Props, prevState: State, snapshot?: IVssComponentContext): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        // After saving to a new branch, default the next save to that same branch
        if (prevProps.defaultBranch && prevProps.defaultBranch !== this.props.defaultBranch) {
            this.setState({
                createNewBranch: false,
                newBranchNameErrorMessage: this._getBranchNameValidationError(this.state.newBranchName)
            });
        }
    }

    public render(): JSX.Element {
        const titleText = this.props.saveAndRun
            ? Resources.SavePanelTitleSaveAndRun
            : this.isValidateAndSavePipelineByDefaultEnabled && this.props.isValidateAndSave
            ? Resources.SavePanelTitleValidateAndSave
            : Resources.SavePanelTitleSave;
        const description =
            this.props.filesAdded && this.props.filesAdded.length > 1
                ? ""
                : format(
                      this.isValidateAndSavePipelineByDefaultEnabled && this.props.isValidateAndSave
                          ? Resources.ValidateAndSavePanelDescription
                          : Resources.SavePanelDescription,
                      this.props.fileName
                  );
        return (
            <div>
                {this.props.isOpen && (
                    <CustomPanel
                        ariaLabel={titleText}
                        size={ContentSize.Medium}
                        onDismiss={this.props.onDismiss}
                        lightDismiss={!this.props.isSaving}
                        defaultActiveElement={getSafeIdSelector(this._commitInputId)}
                    >
                        <PanelHeader
                            titleProps={{ text: titleText, size: TitleSize.Large }}
                            description={description}
                            onDismiss={this.props.onDismiss}
                        />
                        <PanelContent>
                            {this.isValidateAndSavePipelineByDefaultEnabled && this.props.isValidateAndSave
                                ? this._renderValidateAndSaveBody()
                                : this._renderSaveBody()}
                        </PanelContent>
                        {this.isValidateAndSavePipelineByDefaultEnabled && this.props.isValidateAndSave
                            ? this._renderValidateAndSaveFooter()
                            : this._renderSaveFooter()}
                    </CustomPanel>
                )}
            </div>
        );
    }

    private _handleSaveAnywayButton = () => {
        this.setState({ saveAnywayClicked: !this.state.saveAnywayClicked });
    };

    private _renderCommitInputFields(): JSX.Element {
        const isDisabled = this.props.isSaving || this.props.isRunning;
        const defaultBranch = this._normalizeBranchName(this.props.defaultBranch);
        const commitToDefaultText = format(Resources.SavePanelCommitToDefault, defaultBranch);
        const commitToNewBranchText = Resources.SavePanelCommitToNewBranch;
        const newBranchMessage = format(Resources.SavePanelNewBranchMessage, defaultBranch);

        return (
            <React.Fragment>
                <TextField
                    className="commit-message section-content"
                    inputId={this._commitInputId}
                    label={Resources.CommitMessage}
                    disabled={isDisabled}
                    value={this.state.message}
                    onBlur={this._checkMessage}
                    onChange={this._setMessage}
                />
                <TextField
                    className="commit-description section-content"
                    label={Resources.OptionalExtendedDescription}
                    ariaLabel={Resources.OptionalExtendedDescription}
                    disabled={isDisabled}
                    placeholder={Resources.SavePanelCommitDescriptionPlaceholder}
                    value={this.state.description}
                    multiline
                    rows={6}
                    resizable={false}
                    onChange={this._setDescription}
                />
                {this.props.filesAdded && this.props.filesAdded.length > 1 && <FileList files={this.props.filesAdded} />}
                <RadioButtonGroup
                    className="commit-to"
                    selectedButtonId={this.state.createNewBranch ? CommitTo.NewBranch : CommitTo.DefaultBranch}
                    onSelect={this._onCommitToOptionSelect}
                >
                    {[
                        <RadioButton id={CommitTo.DefaultBranch} key={CommitTo.DefaultBranch} text={commitToDefaultText} disabled={isDisabled} />,
                        <RadioButton id={CommitTo.NewBranch} key={CommitTo.NewBranch} text={commitToNewBranchText} disabled={isDisabled} />
                    ]}
                </RadioButtonGroup>
                {this.state.createNewBranch && (
                    <div className="commit-new-branch">
                        <FormItem message={this.state.newBranchNameErrorMessage || newBranchMessage} error={!!this.state.newBranchNameErrorMessage}>
                            <TextField
                                disabled={isDisabled || !this.state.createNewBranch}
                                value={this.state.newBranchName}
                                onBlur={this._checkNewBranchName}
                                onChange={this._setNewBranchName}
                            />
                        </FormItem>
                        {this.props.canCreatePr && (
                            <Checkbox
                                className={"margin-top-8"}
                                disabled={isDisabled || !this.state.createNewBranch}
                                label={Resources.SavePanelCreatePullRequest}
                                checked={this.state.createPullRequest}
                                onChange={this._onCreatePullRequestToggle}
                            />
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    }

    private _renderValidationResult(): JSX.Element {
        const hasValidationErrors = this.props.isValidateAndSave && this.state.fetchedValidationErrors && this.state.validationErrors.length > 0;
        const status = hasValidationErrors ? Statuses.Failed : Statuses.Success;

        return (
            <div className="ci-designer-save-panel-body">
                <label className="bolt-textfield-label">{Resources.ValidateAndSavePanelValidationLabel}</label>
                <div className="section-content flex-row flex-center margin-top-8">
                    <Status {...status} key={hasValidationErrors ? "failed" : "success"} size={StatusSize.m} className="icon-margin" />
                    {hasValidationErrors ? Resources.ValidationResultErrorsDetected : Resources.ValidationResultSuccess + "."}
                </div>
            </div>
        );
    }

    private _renderValidateAndSaveBody(): JSX.Element {
        if (!this.state.fetchedValidationErrors) {
            return (
                <div className="ci-designer-save-panel-body full-size full-width">
                    <Spinner label={Resources.ValidateSpinnerLabel} orientation={SpinnerOrientation.column} className="flex-self-center full-width" />
                </div>
            );
        }

        if (this.state.validationErrors.length > 0 && !this.state.saveAnywayClicked) {
            return (
                <div className="ci-designer-save-panel-body flex-grow">
                    <label className="bolt-textfield-label secondary-text">{Resources.ValidationResultErrorsDetectedLabel}</label>
                    <div className="section-content padding-top-8">
                        <ValidationErrors errors={this.state.validationErrors} />
                    </div>
                </div>
            );
        }

        return (
            <div className="ci-designer-save-panel-body flex-grow">
                {this.props.errorMessage && (
                    <MessageCard className="error-message" severity={MessageCardSeverity.Error}>
                        {this.props.errorMessage}
                    </MessageCard>
                )}
                {this._renderValidationResult()}
                {this._renderCommitInputFields()}
            </div>
        );
    }

    private _renderSaveBody(): JSX.Element {
        return (
            <div className="ci-designer-save-panel-body flex-grow">
                {this.props.errorMessage && (
                    <MessageCard className="error-message" severity={MessageCardSeverity.Error}>
                        {this.props.errorMessage}
                    </MessageCard>
                )}
                {this._renderCommitInputFields()}
            </div>
        );
    }

    private _renderValidateAndSaveFooter = (): JSX.Element => {
        const actionText = this.props.saveAndRun ? Resources.SaveAndRunButtonLabel : Resources.SaveButtonLabel;

        // don't show footer button if it's still validating the pipeline
        const showFooterButtons = !(this.props.isSaving || this.props.isRunning) && this.state.fetchedValidationErrors;

        var saveButton: IButtonProps = {
            className: "primary-action-button",
            disabled: !this._isValidInput(),
            onClick: this._onSaveClick,
            primary: true,
            text: actionText
        };

        if (this.state.validationErrors.length > 0 && !this.state.saveAnywayClicked) {
            saveButton = {
                className: "primary-action-button",
                disabled: !this._isValidInput(),
                onClick: this._handleSaveAnywayButton,
                text: Resources.SaveAnyway
            };
        }

        var cancelButton: IButtonProps = {
            onClick: this.props.onDismiss,
            text: Resources.Cancel
        };

        if (this.state.validationErrors.length > 0 && this.state.saveAnywayClicked) {
            cancelButton = {
                onClick: this._handleSaveAnywayButton,
                text: Resources.ReviewErrors
            };
        }

        const footerButtons = !showFooterButtons ? undefined : this.props.showCancelButton ? [cancelButton, saveButton] : [saveButton];

        const spinnerLabel = Resources.SavingChanges;
        return (
            <PanelFooter buttonProps={footerButtons}>
                {!showFooterButtons && this.props.isSaving && (
                    <div className="ci-designer-save-panel-footer flex-row justify-end">
                        <Spinner className="loading-indicator" orientation={SpinnerOrientation.row} label={spinnerLabel} />
                    </div>
                )}
            </PanelFooter>
        );
    };

    private _renderSaveFooter = (): JSX.Element => {
        const actionText = this.props.saveAndRun ? Resources.SaveAndRunButtonLabel : Resources.SaveButtonLabel;
        const showFooterButtons = !(this.props.isSaving || this.props.isRunning);

        const saveButton: IButtonProps = {
            className: "primary-action-button",
            disabled: !this._isValidInput(),
            onClick: this._onSaveClick,
            primary: true,
            text: actionText
        };

        const cancelButton: IButtonProps = {
            onClick: this.props.onDismiss,
            text: Resources.Cancel
        };
        const footerButtons = !showFooterButtons ? undefined : this.props.showCancelButton ? [cancelButton, saveButton] : [saveButton];

        const spinnerLabel = this.props.isSaving ? Resources.SavingChanges : Resources.CreatingPipeline;
        return (
            <PanelFooter buttonProps={footerButtons}>
                {!showFooterButtons && (
                    <div className="ci-designer-save-panel-footer flex-row justify-end">
                        <Spinner className="loading-indicator" orientation={SpinnerOrientation.row} label={spinnerLabel} />
                    </div>
                )}
            </PanelFooter>
        );
    };

    private _normalizeBranchName(branch: string): string {
        const refsName = "refs/heads/";
        if (branch && branch.startsWith(refsName)) {
            return branch.substring(refsName.length);
        }
        return branch;
    }

    private _checkMessage = () => {
        if (this.state.message.length === 0) {
            this.setState({
                message: Resources.SavePanelCommitMessage
            });
        }
    };

    private _setMessage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, message: string) => {
        this.setState({ message });
    };

    private _setDescription = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, description: string) => {
        this.setState({ description });
    };

    private _checkNewBranchName = () => {
        if (this.state.newBranchName.length === 0) {
            this.setState({
                newBranchName: Resources.SavePanelNewBranchName
            });
        }
    };

    private _setNewBranchName = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newBranchName: string) => {
        this.setState({
            newBranchName,
            newBranchNameErrorMessage: this._getBranchNameValidationError(newBranchName)
        });
    };

    private _onCommitToOptionSelect = (selectedButtonId: string) => {
        this.setState({ createNewBranch: selectedButtonId === CommitTo.NewBranch });
    };

    private _onCreatePullRequestToggle = () => {
        this.setState({ createPullRequest: !this.state.createPullRequest });
    };

    private _onSaveClick = () => {
        this.props.onSave({
            description: this.state.description.trim(),
            createNewBranch: this.state.createNewBranch,
            message: this.state.message.trim(),
            newBranchName: this.state.createNewBranch ? this.state.newBranchName.trim() : "",
            createPullRequest: this.state.createNewBranch && this.props.canCreatePr && this.state.createPullRequest
        });
    };

    private _isValidInput(): boolean {
        return this.state.message.length > 0 && !(this.state.createNewBranch && (!this.state.newBranchName || this.state.newBranchNameErrorMessage));
    }

    /**
     * Returns "" for a valid or empty branch name.
     * Else returns an error message if the branch contains invalid characters.
     * This validation logic is derived from \Tfs\Service\WebAccess\VersionControl\Scripts\RefNameValidator.ts
     * Future work may be to port this as a shared GitRefValidationService in NWP if needed.
     */
    private _getBranchNameValidationError(branchName: string): string {
        if (!branchName || branchName.length === 0) {
            return "";
        }

        // Greater than the max length?
        const fullRefName: string = "refs/heads/" + branchName;
        if (fullRefName.length > this._maxGitRefNameLength) {
            return format(Resources.BranchNameErrorTooLong, this._maxGitRefNameLength);
        }

        // Same as the target branch (case insensitive)?
        const targetBranch = this._normalizeBranchName(this.props.defaultBranch);
        if (equals(branchName, targetBranch, true)) {
            return Resources.BranchNameErrorSameAsTarget;
        }

        let invalidString = null;

        // Contains an invalid Git ref string?
        this._invalidRefNameStrings.some((value: string): boolean => {
            if (branchName.indexOf(value) > -1) {
                invalidString = value;
                return true;
            }
            return false;
        });

        if (invalidString) {
            return format(Resources.BranchNameErrorInvalidCharacter, invalidString);
        }

        // Starts with an invalid Git ref string?
        this._invalidLeadNameStrings.some((value: string): boolean => {
            if (branchName.lastIndexOf(value, 0) === 0) {
                // functionally identical to ES6's startsWith method
                invalidString = value;
                return true;
            }
            return false;
        });

        if (invalidString) {
            return format(Resources.BranchNameErrorInvalidLeadCharacter, invalidString);
        }

        // Ends with an invalid Git ref string?
        this._invalidTrailNameStrings.some((value: string): boolean => {
            const startIndex: number = branchName.length - value.length;
            if (startIndex >= 0 && branchName.indexOf(value, startIndex) === startIndex) {
                invalidString = value;
                return true;
            }
            return false;
        });

        if (invalidString) {
            return format(Resources.BranchNameErrorInvalidTrailCharacter, invalidString);
        }

        return "";
    }
}
